import { useQuery } from '@apollo/client'
import type { SelectOption } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, formatISO, parseISO, startOfMonth } from 'date-fns'
import { Time, time } from '@/lib/time'
import type { TimesheetsModuleConfig } from '@/modules/companies'
import { Module, ModuleOptionsValue } from '@/modules/companies/consts'
import { getUsersHighestPrioritySalaryWorkTime } from '@/modules/companies/util'
import type { YearMonthExportData } from '@/modules/exports'
import { getSalaryDatesForMonth } from '@/modules/exports'
import {
  ExportFormat,
  ExportOption,
  ExportType,
} from '@/modules/exports/consts'
import { USER_SALARY_GROUPS_QUERY } from '@/modules/users/queries'
import { useCallback, useMemo } from 'react'
import { format } from '@/utils/date-fns'
import { notBlankValidation, notEmptyArrayValidation } from '@/utils/forms'
import { useAdmin, useCompany, useModuleConfig, useUser } from '@/utils/hooks'
import { getFullMinutes, isInvalidDate } from '@/utils/time'
import {
  useAbsenceExportMutations,
  useTimesheetExportMutations,
} from './mutations.hooks'
import type {
  ExportAbsensesForm,
  TimeEntryFormValues,
  TimesheetExportForm,
} from './types'
import type {
  TimesheetsDayReportTimeEntry,
  TimesheetsSalaryWorkTime,
} from './types.graphql'

export function useTimeEntryForm(
  entry: TimesheetsDayReportTimeEntry | null,
  salaryWorkTime: TimesheetsSalaryWorkTime,
  initialUser?: string
) {
  const translations = useTranslate({
    noComment: 'common.no-comment',
    error: {
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
      pauseRequired: ['timesheets.error.pause-required', { n: 0 }],
    },
  })
  const user = useUser()

  const { getModuleOptionStatus } = useModuleConfig<TimesheetsModuleConfig>(
    Module.TIMESHEETS
  )
  const overtimeCustomChoice = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_OVERTIME_OPTIONS
  )
  const departmentMandatory = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_DEPARTMENT_SELECT_REQUIRED
  )
  const admin = useAdmin()

  // Default to 8 hours
  const dayOvertimeLimit = salaryWorkTime?.dailyWorkLimit ?? 8 * 60 * 60

  const validateChronology = (start: Date, end: Date) =>
    start.getTime() <= end.getTime() ? null : translations.error.startBeforeEnd

  const validateNotBlank = (comment: string) =>
    notBlankValidation(comment) ? null : translations.error.required

  const departmentValidator = (department: string | null) =>
    departmentMandatory ? validateNotBlank(department ?? '') : null

  const validateCustomOvertime = (
    salaryCodeId: string | null,
    start: Date,
    end: Date
  ) => {
    if (
      !overtimeCustomChoice ||
      (end.getTime() - start.getTime()) / 1000 <= dayOvertimeLimit ||
      !!salaryCodeId
    ) {
      return null
    }
    return translations.error.required
  }

  const form = useForm<TimeEntryFormValues>({
    values: {
      userId: entry ? entry.user.id : admin ? initialUser ?? user.id : user.id,
      start: entry
        ? parseISO(entry.datetimeStart)
        : time('08:00').toDate(new Date()),
      end: entry?.datetimeEnd
        ? parseISO(entry.datetimeEnd)
        : time('16:00').toDate(new Date()),
      facility: entry?.facility?.id ?? null,
      department:
        entry?.department?.id ?? salaryWorkTime?.defaultDepartment?.id ?? null,
      comment: entry?.comment ?? '',
      overtimeSalaryCodeId: entry?.salaryCodeOvertime?.id ?? null,
      salaryCodeId: entry?.salaryCodeOrdinary?.id ?? null,
      diet: entry?.diet ?? false,
      pause: entry
        ? time(entry.pause ?? '00:30').toDate(new Date())
        : time(new Time(0, 0, salaryWorkTime.pauseDuration)).toDate(new Date()),
    },
    validators: {
      userId: userId => validateNotBlank(userId),
      start: (start, { end }) => validateChronology(start, end),
      end: (end, { start }) => validateChronology(start, end),
      comment: validateNotBlank,
      overtimeSalaryCodeId: (overtimeSalaryCodeId, { start, end }) =>
        validateCustomOvertime(overtimeSalaryCodeId, start, end),
      pause: (pause, { start, end }) => {
        const workingMs = end.getTime() - start.getTime()
        const totalWorkingSeconds = workingMs / 1000
        const minimumPause =
          salaryWorkTime.pauseThreshold < totalWorkingSeconds
            ? salaryWorkTime.pauseDuration / 60
            : 0
        const pauseInMinutes = getFullMinutes(pause)
        if (pauseInMinutes >= minimumPause) {
          return null
        }
        return translations.error.pauseRequired({ n: minimumPause })
      },
      department: departmentValidator,
    },
    config: {
      initAsInvalid: true,
    },
  })

  return form
}

export function useTimeEntryModule() {
  const {
    moduleConfig: module,
    moduleActive: active,
    moduleOptions: options,
    getModuleOptionStatus,
  } = useModuleConfig<TimesheetsModuleConfig>(Module.TIMESHEETS)
  const overtimeActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_OVERTIME_OPTIONS
  )

  const overtimeExportActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_DAY_INTERVAL_OVERTIME
  )

  return {
    module,
    active,
    options,
    overtimeActive,
    overtimeExportActive,
  }
}

export function useMassTimesheetExportForm(onCompleted?: () => void) {
  const translations = useTranslate({
    massTimesheets: 'exports.month-export',
    salaryExport: 'exports.salary-export',
    selectUsersError: 'activities.errors.select-multiple-users',
  })

  const me = useUser()
  const company = useCompany()

  const { exportTimesheetsMass, loading: formLoading } =
    useTimesheetExportMutations(onCompleted)

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    updateForm,
  } = useForm<TimesheetExportForm>({
    values: {
      exportType: ExportType.MASS_ENTRY_MONTH,
      exportOption: ExportOption.DOWNLOAD,
      exportFormat: ExportFormat.PDF,
      user: null,
      users: [],
      facility: null,
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      useDateRange: false,
      orderByUserType: false,
      includeAbsences: false,
    },
    validators: {
      users: value =>
        notEmptyArrayValidation(value) ? translations.selectUsersError : null,
    },
    config: {
      storage: {
        storeFormState: true,
        // TODO: Find why this alters from other forms displaying error on mount
        // retrieveFormStateOnMount: true,
        excludeFields: ['users'],
        storeFormStateName: 'Export-timesheets-mass-form',
        transformValues: {
          month: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
        },
      },
    },
  })

  const exportConfig = useMemo<YearMonthExportData>(
    () => ({
      companyId: company.id,
      month: form.month.getMonth(),
      year: form.month.getFullYear(),
      userId: form.user ?? '',
      mailId: me.id,
    }),
    [company.id, form.month, form.user, me.id]
  )

  const { data } = useQuery(USER_SALARY_GROUPS_QUERY, {
    variables: { id: me.id },
  })
  const user = data?.user

  const { moduleConfig } = useModuleConfig(Module.TIMESHEETS)

  const salaryWorkTime = getUsersHighestPrioritySalaryWorkTime(
    user,
    moduleConfig
  )

  const { year, month } = exportConfig
  const salaryDates = getSalaryDatesForMonth(
    salaryWorkTime?.salaryDateStart ?? 27,
    salaryWorkTime?.salaryDateEnd ?? 26,
    year,
    month
  )

  const exportTypes = useMemo<SelectOption<ExportType>[]>(
    () => [
      {
        value: ExportType.MASS_ENTRY_MONTH,
        label: translations.massTimesheets,
      },
      {
        value: ExportType.MASS_ENTRY_SALARY,
        label: `${translations.salaryExport} (${format(
          salaryDates[0],
          'dd.MMMM'
        )}-${format(salaryDates[1], 'dd.MMMM')})`,
      },
    ],
    [salaryDates, translations.massTimesheets, translations.salaryExport]
  )

  const handler = useCallback(
    <K extends keyof TimesheetExportForm>(key: K) => {
      return (value: TimesheetExportForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const getDateInterval = useCallback(
    (startDate: Date, endDate?: Date): [string, string] => {
      if (form.exportType === ExportType.MASS_ENTRY_SALARY) {
        return [formatISO(salaryDates[0]), formatISO(salaryDates[1])]
      }
      if (form.useDateRange) {
        const dateEnd = endDate || new Date()
        return [formatISO(startDate), formatISO(dateEnd)]
      }
      return [
        formatISO(startOfMonth(startDate)),
        formatISO(endOfMonth(startDate)),
      ]
    },
    [form.exportType, form.useDateRange, salaryDates]
  )

  const handleExport = useCallback(
    async (values: TimesheetExportForm) => {
      const date = form.useDateRange
        ? form.dateStart
        : startOfMonth(values.month)
      const [dateStart, dateEnd] = getDateInterval(date, form.dateEnd)
      const orderBy = values.orderByUserType ? 'user_types' : 'last_name'

      void exportTimesheetsMass({
        variables: {
          exportType: values.exportType,
          exportFormat: values.exportFormat,
          userMail: me.id,
          company: company.id,
          dateStart: dateStart,
          dateEnd: dateEnd,
          user: me.id,
          users: form.users,
          orderBy: orderBy,
          includeAbsences: form.includeAbsences,
          exportOption: values.exportOption,
          project: values.facility,
        },
      })
    },
    [
      company.id,
      exportTimesheetsMass,
      form.dateEnd,
      form.dateStart,
      form.includeAbsences,
      form.useDateRange,
      form.users,
      getDateInterval,
      me.id,
    ]
  )

  return {
    form,
    errors,
    loading: formLoading,
    exportTypes,
    handler,
    updateForm,
    submit,
    handleExport,
  }
}

export function useAbsenceReportForm(onCompleted?: () => void) {
  const translations = useTranslate({
    selectUsersError: 'activities.errors.select-multiple-users',
  })

  const me = useUser()
  const company = useCompany()

  const { exportAbsencesMass, loading: mailLoading } =
    useAbsenceExportMutations(onCompleted)

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    updateForm,
  } = useForm<ExportAbsensesForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      exportType: ExportType.ABSENCES,
      exportOption: ExportOption.SAVE,
      exportFormat: ExportFormat.PDF,
      userId: null,
      users: [],
      orderByUserType: false,
      useDateRange: false,
    },
    validators: {
      users: value =>
        notEmptyArrayValidation(value) ? translations.selectUsersError : null,
    },
    config: {
      storage: {
        storeFormState: true,
        // TODO: Find why this alters from other forms displaying error on mount
        // retrieveFormStateOnMount: true,
        storeFormStateName: 'Export-absences-mass-form',
        excludeFields: ['users'],
        transformValues: {
          month: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
        },
      },
    },
  })

  const handler = useCallback(
    <K extends keyof ExportAbsensesForm>(key: K) => {
      return (value: ExportAbsensesForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const getDateInterval = useCallback(
    (startDate: Date, endDate?: Date): [string, string] => {
      if (form.useDateRange) {
        const dateEnd = endDate || new Date()
        return [formatISO(startDate), formatISO(dateEnd)]
      }
      return [
        formatISO(startOfMonth(startDate)),
        formatISO(endOfMonth(startDate)),
      ]
    },
    [form.useDateRange]
  )

  const handleExport = useCallback(
    async (values: ExportAbsensesForm) => {
      const date = form.useDateRange
        ? form.dateStart
        : startOfMonth(values.month)
      const orderBy = values.orderByUserType ? 'user_types' : 'last_name'
      const [dateStart, dateEnd] = getDateInterval(date, form.dateEnd)
      void exportAbsencesMass({
        variables: {
          exportType: values.exportType,
          exportFormat: values.exportFormat,
          userId: me.id,
          companyId: company.id,
          dateStart: dateStart,
          dateEnd: dateEnd,
          users: form.users,
          orderBy: orderBy,
          exportOption: values.exportOption,
        },
      })
    },
    [
      company.id,
      exportAbsencesMass,
      form.dateEnd,
      form.dateStart,
      form.useDateRange,
      form.users,
      getDateInterval,
      me.id,
    ]
  )

  return {
    form,
    errors,
    loading: mailLoading,
    mailLoading,

    handler,
    updateForm,
    submit,
    handleExport,
  }
}
